import {
  PLATFORM_AUTH_HANDOFF_LOGOUT_REQUEST,
  PLATFORM_AUTH_HANDOFF_REQUEST,
  type PlatformAuthHandoffLogoutRequestMessage,
  type PlatformAuthHandoffRequestMessage,
} from "@prismancy/shared";

export type AuthHandoffSession = {
  access_token: string;
  active_workspace?: { id?: string }[];
};

export const isAuthHandoffRequest = (
  value: unknown,
): value is PlatformAuthHandoffRequestMessage => {
  if (!value || typeof value !== "object") return false;
  const message = value as Partial<PlatformAuthHandoffRequestMessage>;
  return (
    message.type === PLATFORM_AUTH_HANDOFF_REQUEST &&
    typeof message.requestId === "string" &&
    message.requestId.length > 0 &&
    message.requestId.length <= 128 &&
    typeof message.token === "string" &&
    message.token.length > 0 &&
    message.token.length <= 20000 &&
    (message.workspaceId === undefined ||
      (typeof message.workspaceId === "string" && message.workspaceId.length <= 128))
  );
};

export const isAuthHandoffLogoutRequest = (
  value: unknown,
): value is PlatformAuthHandoffLogoutRequestMessage => {
  if (!value || typeof value !== "object") return false;
  const message = value as Partial<PlatformAuthHandoffLogoutRequestMessage>;
  return (
    message.type === PLATFORM_AUTH_HANDOFF_LOGOUT_REQUEST &&
    typeof message.requestId === "string" &&
    message.requestId.length > 0 &&
    message.requestId.length <= 128
  );
};

export const isAuthHandoffSession = (
  value: unknown,
): value is AuthHandoffSession => {
  if (!value || typeof value !== "object") return false;
  const session = value as Partial<AuthHandoffSession>;
  return (
    typeof session.access_token === "string" &&
    session.access_token.length > 0 &&
    session.access_token.length <= 20000 &&
    (session.active_workspace === undefined ||
      (Array.isArray(session.active_workspace) &&
        session.active_workspace.every(
          (workspace) =>
            !!workspace &&
            typeof workspace === "object" &&
            (workspace.id === undefined ||
              (typeof workspace.id === "string" && workspace.id.length <= 128)),
        )))
  );
};

export const getAuthHandoffRedirect = (
  search: string,
  currentOrigin: string,
): string => {
  const fallback = "/experiments";
  const redirect = new URLSearchParams(search).get("redirect");
  if (!redirect || !redirect.startsWith("/") || redirect.startsWith("//")) {
    return fallback;
  }

  try {
    const parsed = new URL(redirect, currentOrigin);
    if (parsed.origin !== currentOrigin || parsed.pathname === "/auth/handoff") {
      return fallback;
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return fallback;
  }
};

export const isTopLevelAuthHandoff = (
  search: string,
  isTopLevelWindow: boolean,
): boolean =>
  isTopLevelWindow && new URLSearchParams(search).has("redirect");
